import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import type { ContractBufferSummary, UpdateContractBufferRequest } from '@codepass/shared';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/auth/AuthProvider';
import { Alert, Button, Input, Label, Modal } from '@/components/ui';
import { Money } from '@/components/domain';
import type { ContractDetail } from '@/types';

/**
 * Kortet 'Buffer' på /kontrakter/:id: aftalens samlede buffer og hvor langt fakturaerne er nået.
 *
 * Målet sættes her og gemmes i `codepass_contract_buffer`. Fakturaerne tager det i bidder —
 * hver faktura sin andel af det, der mangler, målt mod aftalens resterende timer og aldrig over
 * en opgaves estimat. Selve fordelingen sker på serveren (`planInvoiceBuffer`).
 */
export function ContractBufferCard({
  contract,
  queryKey,
}: {
  contract: ContractDetail;
  /** Noeglen paa sidens forespoergsel - adressens id, som kan vaere nummeret eller GUID'en. */
  queryKey: readonly unknown[];
}) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const summary: ContractBufferSummary = contract.buffer ?? {
    target_excl_vat: contract.buffer_excl_vat ?? 0,
    collected_excl_vat: 0,
    given_excl_vat: 0,
    missing_excl_vat: contract.buffer_excl_vat ?? 0,
  };
  const key = contract.contract_no ?? contract.id;
  const canEdit = can('invoicing.settings') && Boolean(contract.contract_no);

  /* Optimistisk: kortet står med det nye mål med det samme og rulles tilbage ved fejl. */
  const save = useMutation({
    mutationFn: (body: UpdateContractBufferRequest) =>
      api.put<ContractBufferSummary>(`/contracts/${encodeURIComponent(key)}/buffer`, body),
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: queryKey });
      const previous = queryClient.getQueryData<ContractDetail>(queryKey);
      if (previous) {
        queryClient.setQueryData<ContractDetail>(queryKey, {
          ...previous,
          buffer_excl_vat: body.buffer_excl_vat,
          buffer: {
            ...summary,
            target_excl_vat: body.buffer_excl_vat,
            missing_excl_vat: Math.max(0, body.buffer_excl_vat - summary.collected_excl_vat),
          },
        });
      }
      setOpen(false);
      return { previous };
    },
    onError: (cause, _body, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      setError(cause instanceof ApiError ? cause.message : 'Kunne ikke gemme bufferen.');
    },
    onSuccess: (result) => {
      setError(null);
      const current = queryClient.getQueryData<ContractDetail>(queryKey);
      if (current) {
        queryClient.setQueryData<ContractDetail>(queryKey, {
          ...current,
          buffer_excl_vat: result.target_excl_vat,
          buffer: result,
        });
      }
    },
  });

  /* Hele kroner; komma og punktum som i et dansk beløb. Tomt felt er 0 — ingen buffer. */
  const parsed = (() => {
    const text = draft.trim().replaceAll('.', '').replace(',', '.');
    if (text === '') return 0;
    const value = Number(text);
    return Number.isFinite(value) && value >= 0 ? Math.round(value) : null;
  })();

  const target = summary.target_excl_vat;

  return (
    <div className="card p-4">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs text-ink-muted">Buffer</p>
        {canEdit ? (
          <Button
            variant="ghost"
            size="icon"
            aria-label="Ret aftalens buffer"
            title="Ret aftalens samlede buffer"
            onClick={() => {
              setDraft(target > 0 ? String(target) : '');
              setError(null);
              setOpen(true);
            }}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        ) : null}
      </div>
      <p className="mt-1 text-xl font-semibold tabular">
        <Money value={target} decimals={0} />
      </p>
      <p className="mt-1 text-xs text-ink-muted">
        {target > 0 ? (
          <>
            Opsamlet <Money value={summary.collected_excl_vat} decimals={0} />
            {summary.missing_excl_vat > 0 ? (
              <>
                {' '}
                · mangler <Money value={summary.missing_excl_vat} decimals={0} />
              </>
            ) : (
              ' · nået'
            )}
          </>
        ) : summary.collected_excl_vat > 0 ? (
          <>
            Ingen samlet buffer · opsamlet <Money value={summary.collected_excl_vat} decimals={0} />
          </>
        ) : (
          'Ingen buffer'
        )}
        {summary.given_excl_vat > 0 ? (
          <>
            {' '}
            · givet væk <Money value={summary.given_excl_vat} decimals={0} />
          </>
        ) : null}
      </p>
      {error ? (
        <Alert tone="error" className="mt-2">
          {error}
        </Alert>
      ) : null}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Aftalens buffer"
        description="Den samlede buffer, aftalens fakturaer skal nå op på."
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Annullér
            </Button>
            <Button
              disabled={parsed === null || save.isPending}
              onClick={() => parsed !== null && save.mutate({ buffer_excl_vat: parsed })}
            >
              Gem
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm">
          <div>
            <Label htmlFor="contract-buffer">Beløb ekskl. moms (DKK)</Label>
            <Input
              id="contract-buffer"
              inputMode="decimal"
              autoComplete="off"
              placeholder="0"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && parsed !== null) {
                  save.mutate({ buffer_excl_vat: parsed });
                }
              }}
            />
            {parsed === null ? (
              <p className="mt-1 text-xs text-danger">Skriv et beløb i hele kroner.</p>
            ) : null}
          </div>
          <p className="text-xs text-ink-muted">
            Hver faktura lægger sin andel af det, der mangler, ud på opgaverne som ekstra timer —
            fordelt over de timer, aftalen har tilbage af sit loft. En opgave får aldrig flere timer
            end sit estimat, og en opgave over sit estimat får ingenting. Kunden ser ingen
            bufferlinje. 0 fjerner bufferen.
          </p>
        </div>
      </Modal>
    </div>
  );
}
